import { formatDate } from '@angular/common';
import { BorrowerModel } from '../../cmg-1003/models/borrower.model';
import { EmploymentIncomeExpenseModel } from '../../cmg-1003/models/employment-income-expense.model';
import { LoanDataModel, LoanPurposeType, MortgageType } from '../../cmg-1003/models/loan-information.model';
import { PropertyInformationModel } from '../../cmg-1003/models/property-information.model';
import { ILoanStatusCard, LoanStatusCardConfig, LoanStatusCardType } from './loan-status-card.model';
import { LoanItemStatus, LoanItemStatusIconType } from '../../shared/models';
import { RefinancePurposeFha, RefinancePurposeVa } from '../../cmg-1003/models';
import { nonNullish } from '../../shared';

export const respaRequiredFieldCount = 6;
export const respaRequiredFieldCountFhaStreamlineOrVaIrrrl = 5;

export enum respaFields {
  Name = 'Name',
  Income = 'Income',
  SSN = 'SSN',
  Address = 'Address',
  AppraisedValue = 'AppraisedValue',
  BaseLoanAmount = 'BaseLoanAmount',
}

export enum RespaStatusType {
  Completed = 'Completed',
  NotCompleted = 'NotCompleted',
  FhaVaRefinanceCompleted = 'FhaVaRefinanceCompleted',
}

export const getRESPACompleteFields = (
  borrowers: BorrowerModel[],
  allApplicationsIncome: EmploymentIncomeExpenseModel[],
  property: PropertyInformationModel,
  loanInfo: LoanDataModel,
  isFhaStreamlineOrVaIrrrl?: boolean,
  totalNriFromReo?: number,
): string[] => {
  let fields: string[] = [];

  borrowers?.forEach((b) => {
    let fieldsCurrent: string[] = [];

    // Name Check:
    // Borrower Name element will be considered complete when Borrower Last Name is populated *for any borrower on any application*
    if (b?.lastName?.trim().length > 0) {
      fieldsCurrent.push(respaFields.Name);
    }

    if (!isFhaStreamlineOrVaIrrrl) {
      // Income Check:
      // Employment income OR NRI from REO (Retained Rental) satisfies the income piece.
      const incomeComplete =
        hasIncomeGreaterThanOrEqualToZero(allApplicationsIncome) ||
        (totalNriFromReo !== null && totalNriFromReo !== undefined && totalNriFromReo > 0);

      if (incomeComplete && !fieldsCurrent.includes(respaFields.Income)) {
        fieldsCurrent.push(respaFields.Income);
      } else {
        fieldsCurrent = fieldsCurrent.filter((f) => f !== respaFields.Income);
      }
    }

    // SSN Check:
    // SSN (Social Security Number) element will be considered complete only when Borrower Social Security Number (or TIN) is populated *for any borrower on any application*
    if (b?.ssn?.trim().length > 0) {
      fieldsCurrent.push(respaFields.SSN);
    }

    // Property Address:
    if (property?.street !== null && property?.street?.trim().length > 0 && property?.street?.trim().toLocaleLowerCase() !== 'tbd') {
      fieldsCurrent.push(respaFields.Address);
    }

    // Appraised Value:
    if (loanInfo?.appraisalValue > 0 || loanInfo?.salesPrice > 0) {
      fieldsCurrent.push(respaFields.AppraisedValue);
    }

    // Base Loan Amount:
    if (loanInfo?.baseLoanAmount > 0) {
      fieldsCurrent.push(respaFields.BaseLoanAmount);
    }

    fields = [...new Set([...fields, ...fieldsCurrent])];
    fieldsCurrent = [];
  });

  return fields;
};

/**
 * Income is considered complete if there exists any income >= $0.00 for any borrower on any application
 */
export const hasIncomeGreaterThanOrEqualToZero = (allApplicationsIncome: EmploymentIncomeExpenseModel[]): boolean => {
  for (let i = 0; i < allApplicationsIncome?.length; i++) {
    const incomeZeroOrGreater = allApplicationsIncome[i]?.grossMonthlyIncomes?.find(
      (income) => !income.isDeleted && nonNullish(income.amount) && income.amount >= 0,
    );

    if (incomeZeroOrGreater) {
      return true;
    }
  }

  return false;
};

export const isFhaRefiStreamlineVaRefiIRRRL = (loanInfo: LoanDataModel) => {
  return (
    (loanInfo?.mortgageAppliedFor === MortgageType.fha &&
      loanInfo?.loanPurpose === LoanPurposeType.refinance &&
      (loanInfo?.refinancePurposeFha === RefinancePurposeFha.StreamlinedWithAppraisal ||
        loanInfo?.refinancePurposeFha === RefinancePurposeFha.StreamlinedWithoutAppraisal)) ||
    (loanInfo?.mortgageAppliedFor === MortgageType.va &&
      loanInfo?.loanPurpose === LoanPurposeType.refinance &&
      loanInfo?.refinancePurposeVa === RefinancePurposeVa.IRRRL)
  );
};

export const getRESPAStatus = (
  borrowers: BorrowerModel[],
  employmentIncome: EmploymentIncomeExpenseModel[],
  property: PropertyInformationModel,
  loanInfo: LoanDataModel,
  isFhaStreamlineOrVaIrrrl?: boolean,
  totalNriFromReo?: number,
): ILoanStatusCard => {
  const cardBase = LoanStatusCardConfig.find((c) => c.cardType === LoanStatusCardType.TRID);
  const card = <ILoanStatusCard>{ ...cardBase, cardType: LoanStatusCardType.TRID };
  const fieldList = getRESPACompleteFields(borrowers, employmentIncome, property, loanInfo, isFhaStreamlineOrVaIrrrl, totalNriFromReo);
  const requiredFieldCount = isFhaStreamlineOrVaIrrrl ? respaRequiredFieldCountFhaStreamlineOrVaIrrrl : respaRequiredFieldCount;

  const hasValidApplicationDate = isValidDate(loanInfo?.applicationDate);
  const completedFieldCount = fieldList.length;

  if (hasValidApplicationDate) {
    card.status = LoanItemStatus.Complete;
    card.icon = LoanItemStatusIconType.Circle;
    card.descriptionHtml = `App Date <b>${formatDate(loanInfo?.applicationDate, 'M/d/yy', 'en-US')}</b>`;

    if (completedFieldCount < requiredFieldCount) {
      card.status = LoanItemStatus.Warning;
      card.icon = LoanItemStatusIconType.Diamond;
      card.descriptionHtml += `<br><b>${completedFieldCount}</b> of <b>${requiredFieldCount}</b> TRID`;
    }
  } else if (completedFieldCount === requiredFieldCount) {
    card.status = LoanItemStatus.Warning;
    card.icon = LoanItemStatusIconType.Diamond;
    card.descriptionHtml = `App Date <b>Not Entered</b><br><b>${completedFieldCount}</b> of <b>${requiredFieldCount}</b> TRID`;
  } else if (completedFieldCount === requiredFieldCount - 1) {
    card.status = LoanItemStatus.Warning;
    card.icon = LoanItemStatusIconType.Diamond;
    card.descriptionHtml = `<b>${completedFieldCount}</b> of <b>${requiredFieldCount}</b> TRID`;
  } else {
    card.status = LoanItemStatus.NotComplete;
    card.icon = LoanItemStatusIconType.EmptyCircle;
    card.descriptionHtml = `<b>${completedFieldCount}</b> of <b>${requiredFieldCount}</b> TRID`;
  }

  return card;
};

const isValidDate = (dateString: string): boolean => {
  const d = new Date(dateString ?? undefined);
  return d instanceof Date && !isNaN(d.getTime());
};
